// ═══════════════════════════════════════════════════════════
// Tasks API
// ═══════════════════════════════════════════════════════════

import { queryRecords, createRecord, updateRecord, deleteRecord } from './client';
import { TABLES } from './tables';
import { FIELDS } from './fields';
import { createAuditEntry } from './auditLog';

export type TaskType =
  | 'acknowledge' | 'gather' | 'review' | 'redact'
  | 'legalReview' | 'approve' | 'invoice' | 'release' | 'other';

export type TaskStatus = 'open' | 'inProgress' | 'complete' | 'blocked';

export interface Task {
  recordId:       number;
  requestId:      number;
  type:           TaskType;
  title:          string;
  description?:   string;
  assignedUserId?:string;
  assignedDeptId?:string;
  dueDate?:       string;
  status:         TaskStatus;
  isBlocking:     boolean;
  completedBy?:   string;
  completedAt?:   string;
}

const F = FIELDS.tasks;

function rowToTask(row: Record<string, unknown>): Task {
  return {
    recordId:        row['record_id'] as number,
    requestId:       Number(row[F.requestId] ?? 0),
    type:            (row[F.type] as TaskType) ?? 'other',
    title:           String(row[F.title] ?? ''),
    description:     row[F.description] as string | undefined,
    assignedUserId:  row[F.assignedUserId] as string | undefined,
    assignedDeptId:  row[F.assignedDeptId] as string | undefined,
    dueDate:         row[F.dueDate] as string | undefined,
    status:          (row[F.status] as TaskStatus) ?? 'open',
    isBlocking:      row[F.isBlocking] === true || row[F.isBlocking] === 'true',
    completedBy:     row[F.completedBy] as string | undefined,
    completedAt:     row[F.completedAt] as string | undefined,
  };
}

export async function listTasksForRequest(requestRecordId: number): Promise<Task[]> {
  const { records } = await queryRecords(TABLES.tasks, {
    filters: [{ field: F.requestId, operator: 'eq', value: requestRecordId }],
    sort: { field: F.dueDate, direction: 'asc' },
    limit: 200,
  });
  return (records as Record<string, unknown>[]).map(rowToTask);
}

export interface CreateTaskInput {
  requestId:       number;
  type:            TaskType;
  title:           string;
  description?:    string;
  assignedUserId?: string;
  assignedDeptId?: string;
  dueDate?:        string;
  isBlocking?:     boolean;
}

export async function createTask(input: CreateTaskInput, actorEmail?: string): Promise<Task> {
  const row = await createRecord(TABLES.tasks, {
    [F.requestId]:       input.requestId,
    [F.type]:            input.type,
    [F.title]:           input.title,
    [F.description]:     input.description ?? '',
    [F.assignedUserId]:  input.assignedUserId ?? '',
    [F.assignedDeptId]:  input.assignedDeptId ?? '',
    [F.dueDate]:         input.dueDate ?? '',
    [F.status]:          'open',
    [F.isBlocking]:      input.isBlocking ?? false,
  });
  await createAuditEntry({
    requestId:  input.requestId,
    userEmail:  actorEmail ?? '',
    action:     'task.created',
    details:    JSON.stringify({ title: input.title, type: input.type }),
  });
  return rowToTask(row as Record<string, unknown>);
}

export async function updateTaskStatus(
  recordId: number,
  status: TaskStatus,
  actorEmail?: string,
  requestId?: number,
): Promise<Task> {
  const patch: Record<string, unknown> = { [F.status]: status };
  if (status === 'complete') {
    patch[F.completedBy] = actorEmail ?? '';
    patch[F.completedAt] = new Date().toISOString();
  }
  const row = await updateRecord(TABLES.tasks, recordId, patch);
  if (requestId) {
    await createAuditEntry({
      requestId,
      userEmail: actorEmail ?? '',
      action:    'task.updated',
      details:   JSON.stringify({ taskId: recordId, status }),
    });
  }
  return rowToTask(row as Record<string, unknown>);
}

export async function deleteTask(recordId: number): Promise<void> {
  await deleteRecord(TABLES.tasks, recordId);
}

export async function updateTask(
  recordId: number,
  input: Partial<CreateTaskInput>,
): Promise<Task> {
  const patch: Record<string, unknown> = {};
  if (input.title           !== undefined) patch[F.title]           = input.title;
  if (input.description     !== undefined) patch[F.description]     = input.description;
  if (input.assignedUserId  !== undefined) patch[F.assignedUserId]  = input.assignedUserId;
  if (input.assignedDeptId  !== undefined) patch[F.assignedDeptId]  = input.assignedDeptId;
  if (input.dueDate         !== undefined) patch[F.dueDate]         = input.dueDate;
  if (input.isBlocking      !== undefined) patch[F.isBlocking]      = input.isBlocking;
  const row = await updateRecord(TABLES.tasks, recordId, patch);
  return rowToTask(row as Record<string, unknown>);
}
